/**
 * How InOut's answer reads on a screen — the same selection on the web, on
 * the phone and in the calculator, so they never disagree about which rule
 * heads the answer, which binds, or in which order the rules are shown.
 * Presentation only: every number is the engine's.
 */
import type { InOutCalculationAnswer } from '@tonnie/types';
import type { TFunction } from 'i18next';
import { dayOf, type IsoDate } from './dates';
import type { Duration } from './duration';
import type {
  Accrual,
  Direction,
  Geography,
  InOutAcquisitionClock,
  InOutAwayGeography,
  InOutClock,
  InOutEvaluation,
  InOutRuleWindow,
  InOutWhereToday,
} from './types';
import {
  INOUT_BINDING_REASON_KEY,
  INOUT_DURATION_KEY,
  INOUT_METER_DETAIL_KEY,
  INOUT_METER_NAME_KEY,
  inoutClockHasNumber,
  inoutClockNameKey,
  type InOutLimitWindowKind,
} from './vocabulary';

export type InOutCountedClock = InOutClock & { value: number; limit: number };

/**
 * The order the rules are read in — how much each says about the title
 * (owner, 20 Oct 2026): the days away from the title's country first — the
 * counter InOut is born from — then each single trip from it; a rule of the
 * EU, of the host's own days, and last the days in the other Schengen states
 * (the 90/180), the least relevant to the title even when its number is the
 * smallest. A limit on absence before a minimum stay; the total before each
 * trip. Never the order of urgency — that is `binding`'s, said beside it.
 */
const GEOGRAPHY_RELEVANCE = {
  OUTSIDE_HOST: 0,
  OUTSIDE_EU: 1,
  INSIDE_HOST: 2,
  SCHENGEN_EXCLUDING_HOST: 3,
} as const satisfies Record<Geography, number>;
const ACCRUAL_RELEVANCE = { CUMULATIVE: 0, CONSECUTIVE: 1 } as const satisfies Record<
  Accrual,
  number
>;
const DIRECTION_RELEVANCE = { MAX_ABSENCE: 0, MIN_PRESENCE: 1 } as const satisfies Record<
  Direction,
  number
>;

/**
 * The rules shown, most relevant first (see above). Of two readings of one
 * rule that disagree, the one planned by takes the rule's place and the other
 * follows it; rules the order cannot tell apart keep the country's order.
 */
export function inoutClocksByRelevance(
  evaluation: Pick<InOutEvaluation, 'clocks' | 'divergences'>
): InOutClock[] {
  // A reading not planned by sits right after the reading it disagrees with —
  // the same rule, read twice — never ranked by its own shape.
  const plannedPartner = new Map(
    evaluation.divergences.flatMap((d) =>
      d.constraintIds
        .filter((id) => id !== d.planningConstraintId)
        .map((id) => [id, d.planningConstraintId] as const)
    )
  );
  const byId = new Map(evaluation.clocks.map((clock) => [clock.constraintId, clock]));
  const rank = (clock: InOutClock) => {
    const partnerId = plannedPartner.get(clock.constraintId);
    const ranked = (partnerId && byId.get(partnerId)) || clock;
    return [
      DIRECTION_RELEVANCE[ranked.direction],
      GEOGRAPHY_RELEVANCE[ranked.geography],
      ACCRUAL_RELEVANCE[ranked.accrual],
      partnerId ? 1 : 0,
    ];
  };
  return evaluation.clocks
    .filter(isShown)
    .map((clock, index) => ({ clock, index, rank: rank(clock) }))
    .sort((a, b) => {
      for (let i = 0; i < a.rank.length; i++) {
        const d = (a.rank[i] ?? 0) - (b.rank[i] ?? 0);
        if (d !== 0) return d;
      }
      return a.index - b.index;
    })
    .map(({ clock }) => clock);
}

/**
 * The title's limits on absence, side by side (owner, 21 Oct 2026, after the
 * meeting with Ei!): every rule the engine reads one trip against
 * (`oneTrip.constraintIds` — the rules that cap days away from the title's
 * country), most relevant first. For a temporary permit, the 8 months in
 * total and the 6 months in a row; for a permanent one, the 30 months in any
 * 36 and the 24 in a row; for the EU long-term status, 6 years away from the
 * country and 12 months outside the EU. A title with one such rule has one;
 * none, none — the 90/180 is never among them (`INOUT_AWAY_FROM_HOST`).
 */
export function inoutAbsenceLimits(
  evaluation: Pick<InOutEvaluation, 'clocks' | 'divergences' | 'oneTrip'>
): InOutClock[] {
  const ids = new Set(evaluation.oneTrip?.constraintIds ?? []);
  return inoutClocksByRelevance(evaluation).filter((clock) => ids.has(clock.constraintId));
}

/**
 * One of the title's limits on absence, as a meter beside the other (owner,
 * 22 Oct 2026): the days one trip has taken of it — for a limit in a row the
 * trip under way (0 at home: a past absence does not shorten the next one),
 * for a limit in total the days the window has counted — against the limit,
 * the rest, and the share taken (0–1, the meter's fill). `definesAnswer`: the
 * one the answer's number comes from — marked with the answer's hourglass,
 * only when there are two to tell apart. None for a regime a person checks.
 */
export interface InOutLimitCounter {
  clock: InOutClock;
  /** None for a rule with no number (AC-2). */
  figures: { taken: number; limit: number; left: number; share: number } | null;
  definesAnswer: boolean;
}

export function inoutLimitCounters(evaluation: InOutEvaluation): InOutLimitCounter[] {
  // A regime a person checks shows no counter at all (AC-2) — the answer says why.
  if (evaluation.humanCheck) return [];
  const limits = inoutAbsenceLimits(evaluation);
  const answerId = evaluation.oneTrip?.reading?.constraintId ?? null;
  return limits.map((clock) => ({
    clock,
    figures: limitFigures(clock),
    definesAnswer: limits.length > 1 && clock.constraintId === answerId,
  }));
}

/**
 * A meter's words: its name — "In total", "In a row" (outside the EU when
 * that is where it counts) — and the limit as the law writes it, short:
 * "8 months in the permit", "6 months per trip". None for a rule with no
 * number: its limit is not said either (AC-2).
 */
export function inoutLimitMeterText(
  clock: InOutClock,
  t: TFunction,
  formatNumber: (value: number) => string
): { name: string; detail: string | null } {
  const geography: InOutAwayGeography =
    clock.geography === 'OUTSIDE_EU' ? 'OUTSIDE_EU' : 'OUTSIDE_HOST';
  const name = t(INOUT_METER_NAME_KEY[geography][clock.accrual]);
  if (!inoutClockHasNumber(clock) || !clock.written) return { name, detail: null };
  const { limit, window } = clock.written;
  const span = windowSpan(window);
  return {
    name,
    detail: t(INOUT_METER_DETAIL_KEY[clock.accrual][windowKindOf(window, span)], {
      limit: durationText(limit, t, formatNumber),
      window: span ? durationText(span, t, formatNumber) : '',
    }),
  };
}

/**
 * Why one trip can last what the answer says, when the title has two limits
 * on absence and one leaves less than the other (owner, 22 Oct 2026 — "why 179
 * and not 183?"): the limit the number comes from and the other one, each
 * with what it leaves. None when there is one limit, when either has no
 * number, or when both leave the same.
 */
export function inoutAnswerWhy(
  evaluation: InOutEvaluation
): { defining: InOutLimitCounter; other: InOutLimitCounter } | null {
  const counters = inoutLimitCounters(evaluation);
  if (counters.length !== 2 || counters.some((c) => c.figures === null)) return null;
  const defining = counters.find((c) => c.definesAnswer);
  const other = counters.find((c) => !c.definesAnswer);
  if (!defining?.figures || !other?.figures) return null;
  return defining.figures.left < other.figures.left ? { defining, other } : null;
}

/**
 * The rule the answer's number is read from — the one a screen marks with the
 * answer's hourglass (owner, 22 Oct 2026: one mark, never a second "first").
 * None for an answer with no rule.
 */
export function inoutAnswerClock(answer: InOutAnswer): InOutClock | null {
  switch (answer.kind) {
    case 'oneTrip':
    case 'presenceNeeded':
    case 'ruleLeft':
    case 'beyond':
    case 'unresolved':
      return answer.clock;
    case 'humanCheck':
    case 'noRule':
      return null;
    default: {
      const unreachable: never = answer;
      throw new Error(`Unknown InOut answer: ${String(unreachable)}`);
    }
  }
}

/**
 * The stay in the other Schengen states — the 90/180 — said as its condition
 * beside a one-trip answer (owner, 22 Oct 2026): it binds only a trip to
 * another Schengen state, so it is never the answer's number nor "first". The
 * days still allowed there. None when the title has no such rule or it has no
 * number (AC-2: its card says why), and none beside any other answer — a
 * title with no limit on absence reads it as its answer, and a rule already
 * past its limit is the answer itself (`inoutAnswer`).
 */
export interface InOutOtherSchengenStay {
  clock: InOutCountedClock;
  days: number;
}

export function inoutOtherSchengenStay(evaluation: InOutEvaluation): InOutOtherSchengenStay | null {
  if (inoutAnswer(evaluation).kind !== 'oneTrip') return null;
  const clock = inoutShownClocks(evaluation).clocks.find(
    (c) => c.geography === 'SCHENGEN_EXCLUDING_HOST' && c.direction === 'MAX_ABSENCE'
  );
  if (!clock || !inoutClockHasNumber(clock)) return null;
  return { clock, days: clock.remaining?.certain ?? 0 };
}

/**
 * A limit's counter figures, as the engine counted them: the trip under way
 * (`currentRun`) of a limit in a row, the window's count (`value`) of a limit
 * in total, and the room (`remaining`). None where the engine gave none.
 */
function limitFigures(clock: InOutClock): InOutLimitCounter['figures'] {
  if (!inoutClockHasNumber(clock) || !clock.remaining) return null;
  const taken = clock.accrual === 'CONSECUTIVE' ? clock.currentRun : clock.value;
  if (taken === undefined) return null;
  return {
    taken,
    limit: clock.limit,
    left: clock.remaining.certain,
    share: clock.limit > 0 ? Math.min(1, taken / clock.limit) : 1,
  };
}

export interface InOutHeadlineView {
  /**
   * The most relevant rule shown — "Days away from Portugal" (owner, 20 Oct
   * 2026): the timeline's window — or null when no rule applies.
   */
  clock: InOutClock | null;
  /**
   * The rule that reaches its limit first (the engine's `binding`) when it is
   * none of the rules the answer is read from (the title's limits on absence,
   * or the headline's rule without them) — the 90/180, say: said beside the
   * answer, never hidden by the order of relevance, never hiding a counter.
   */
  firstToLimit: InOutClock | null;
}

export function inoutHeadlineView(evaluation: InOutEvaluation): InOutHeadlineView {
  const clock = inoutClocksByRelevance(evaluation)[0] ?? null;
  const limits = inoutAbsenceLimits(evaluation);
  const answeredBy = new Set(
    limits.length > 0 ? limits.map((c) => c.constraintId) : clock ? [clock.constraintId] : []
  );
  const binding = evaluation.binding
    ? (evaluation.clocks.find((c) => c.constraintId === evaluation.binding?.constraintId) ?? null)
    : null;
  return {
    clock,
    firstToLimit: binding && !answeredBy.has(binding.constraintId) ? binding : null,
  };
}

/**
 * How much of a rule's limit is taken from today, 0–100 — what a rule's bar
 * fills with, the complement of the ring around the days left: the room is
 * the engine's `remaining` (for a limit on each trip, the trip in progress —
 * empty while the person is in the country), never "longest past ÷ limit".
 */
export function inoutClockUsedPercent(clock: InOutCountedClock): number {
  if (clock.limit <= 0) return 0;
  const used = clock.remaining
    ? clock.limit - clock.remaining.certain
    : Math.min(clock.value, clock.limit);
  return Math.max(0, Math.min(100, Math.round((used / clock.limit) * 100)));
}

/** How much of a rule's limit is still left, 0–100 — what its hourglass and its ring hold. */
export function inoutClockLeftPercent(clock: InOutCountedClock): number {
  return 100 - inoutClockUsedPercent(clock);
}

/**
 * The one answer at the top of InOut — the question the person came with:
 *
 *   oneTrip         how long ONE trip from today can last before any of the
 *                   title's limits on absence is passed (owner, 21 Oct 2026):
 *                   the engine's `oneTrip` — the least room among them, the
 *                   limit it comes from (`clock`), whether it is the rest of
 *                   a trip under way, the geography it is said in (away from
 *                   the country, or outside the EU);
 *   presenceNeeded  how many more days they need in the country — a title
 *                   with no limit on absence, by its headline rule;
 *   ruleLeft        the days left of the headline rule — a title whose only
 *                   rule with a number limits something else (a stay in the
 *                   other Schengen states);
 *   beyond          a rule is already past its limit — whichever it is: a
 *                   limit passed is never left under a count of days left;
 *   unresolved      a rule the answer is read from has no number yet (AC-2);
 *   humanCheck      a person checks this regime — no number (AC-2);
 *   noRule          no limit applies today.
 */
export type InOutAnswer =
  | {
      kind: 'oneTrip';
      clock: InOutCountedClock;
      days: number;
      underWay: boolean;
      geography: InOutAwayGeography;
    }
  | { kind: 'presenceNeeded'; clock: InOutCountedClock; days: number }
  | { kind: 'ruleLeft'; clock: InOutCountedClock; days: number }
  | { kind: 'beyond'; clock: InOutCountedClock }
  | { kind: 'unresolved'; clock: InOutClock }
  | { kind: 'humanCheck' }
  | { kind: 'noRule' };

export function inoutAnswer(evaluation: InOutEvaluation): InOutAnswer {
  if (evaluation.humanCheck) return { kind: 'humanCheck' };
  const binding = evaluation.binding
    ? evaluation.clocks.find((c) => c.constraintId === evaluation.binding?.constraintId)
    : undefined;
  if (binding?.status === 'BEYOND' && inoutClockHasNumber(binding)) {
    return { kind: 'beyond', clock: binding };
  }
  const limits = inoutAbsenceLimits(evaluation);
  if (limits.length > 0) {
    const noNumber = limits.find((c) => !inoutClockHasNumber(c));
    if (noNumber) return { kind: 'unresolved', clock: noNumber };
    const passed = limits.find((c) => c.status === 'BEYOND');
    if (passed && inoutClockHasNumber(passed)) return { kind: 'beyond', clock: passed };
    const reading = evaluation.oneTrip?.reading;
    const first = reading && limits.find((c) => c.constraintId === reading.constraintId);
    if (reading && first && inoutClockHasNumber(first)) {
      return {
        kind: 'oneTrip',
        clock: first,
        days: reading.days.certain,
        underWay: evaluation.oneTrip?.underWay ?? false,
        geography: reading.geography,
      };
    }
  }
  const { clock } = inoutHeadlineView(evaluation);
  if (!clock) return { kind: 'noRule' };
  if (!inoutClockHasNumber(clock)) return { kind: 'unresolved', clock };
  if (clock.status === 'BEYOND') return { kind: 'beyond', clock };
  const days = clock.remaining?.certain ?? 0;
  return clock.direction === 'MIN_PRESENCE'
    ? { kind: 'presenceNeeded', clock, days }
    : { kind: 'ruleLeft', clock, days };
}

/**
 * The answer's number and the limit it is the rest of — the band's figure and
 * hourglass, the phone's circle: the days, the limit, and how much of it is
 * left (0–100). None for an answer with no number.
 */
export function inoutAnswerFigure(
  answer: InOutAnswer
): { days: number; limit: number; leftPercent: number } | null {
  switch (answer.kind) {
    case 'oneTrip':
    case 'presenceNeeded':
    case 'ruleLeft':
      return {
        days: answer.days,
        limit: answer.clock.limit,
        leftPercent: inoutClockLeftPercent(answer.clock),
      };
    case 'beyond':
    case 'unresolved':
    case 'humanCheck':
    case 'noRule':
      return null;
    default: {
      const unreachable: never = answer;
      throw new Error(`Unknown InOut answer: ${String(unreachable)}`);
    }
  }
}

/**
 * The answer as the calculator hands it on (sign-up says it back): the
 * number and what it is, never the rule or the record. No number to say in
 * the calculator's own words — a rule's days left, unresolved, a person
 * checks, no rule — is `none`.
 */
export function inoutAnswerSummary(evaluation: InOutEvaluation): InOutCalculationAnswer {
  const answer = inoutAnswer(evaluation);
  switch (answer.kind) {
    case 'oneTrip':
      return {
        kind: 'oneTrip',
        days: answer.days,
        underWay: answer.underWay,
        geography: answer.geography,
      };
    case 'presenceNeeded':
      return { kind: 'presenceNeeded', days: answer.days };
    case 'beyond':
      return { kind: 'beyond' };
    case 'ruleLeft':
    case 'unresolved':
    case 'humanCheck':
    case 'noRule':
      return { kind: 'none' };
    default: {
      const unreachable: never = answer;
      throw new Error(`Unknown InOut answer: ${String(unreachable)}`);
    }
  }
}

/**
 * An answer with a number, in words — "You can be away from Portugal for up
 * to 183 days in one trip", "You can stay outside the EU 300 more days on this
 * trip", "You need 30 more days in Portugal": the heading, the phone's circle
 * and what sign-up says back, from one place.
 */
export function inoutDaysAnswerText(
  answer:
    | { kind: 'oneTrip'; days: number; underWay: boolean; geography: InOutAwayGeography }
    | { kind: 'presenceNeeded'; days: number },
  {
    t,
    formatNumber,
    country,
  }: { t: TFunction; formatNumber: (value: number) => string; country: string }
): string {
  const days = formatNumber(answer.days);
  switch (answer.kind) {
    case 'oneTrip': {
      // Where the limit that caps it counts, and whether the trip is under way.
      const said = { count: answer.days, days, country };
      switch (answer.geography) {
        case 'OUTSIDE_HOST':
          return answer.underWay
            ? t('inout.answer.thisTrip', said)
            : t('inout.answer.oneTrip', said);
        case 'OUTSIDE_EU':
          return answer.underWay
            ? t('inout.answer.thisTripOutsideEu', said)
            : t('inout.answer.oneTripOutsideEu', said);
        default: {
          const unreachable: never = answer.geography;
          throw new Error(`Unknown geography: ${String(unreachable)}`);
        }
      }
    }
    case 'presenceNeeded':
      return t('inout.answer.presenceNeeded', { count: answer.days, days, country });
    default: {
      const unreachable: never = answer;
      throw new Error(`Unknown InOut answer: ${String(unreachable)}`);
    }
  }
}

/**
 * What the answer's number counts, under it in large — "days in one trip",
 * "more days on this trip", "presence days needed", "days left": the band's
 * figure and the phone's circle. None for an answer with no number.
 */
export function inoutAnswerUnitText(answer: InOutAnswer, t: TFunction): string | null {
  switch (answer.kind) {
    case 'oneTrip':
      return answer.underWay ? t('inout.answer.unit.thisTrip') : t('inout.answer.unit.oneTrip');
    case 'presenceNeeded':
      return t('inout.banner.daysStillNeeded');
    case 'ruleLeft':
      return t('inout.banner.daysLeft');
    case 'beyond':
    case 'unresolved':
    case 'humanCheck':
    case 'noRule':
      return null;
    default: {
      const unreachable: never = answer;
      throw new Error(`Unknown InOut answer: ${String(unreachable)}`);
    }
  }
}

/**
 * The answer in words, for the web, the phone and the calculator:
 *
 *   • `title` and `subtitle` — the band's heading and the smaller line under
 *     it. One trip from today is a short heading that holds one line — "You
 *     can be away up to 144 days" — and the line says the rest: "In one trip
 *     away from Portugal, from today, without passing any limit of your
 *     permit" (owner, 22 Oct 2026 — TestIt's band: a short tagline, the
 *     detail under it in a smaller face, so the heroes' bands match);
 *   • `sentence` — the answer as one sentence, where it is read alone (a
 *     screen reader on the phone's circle, "What if I travel?"): "You can be
 *     away from Portugal for up to 144 days in one trip".
 *
 * The limits it is read from are the meters beside it; a rule that applies
 * only on some trips (the 90/180) is said as its condition, apart
 * (`inoutOtherSchengenStay`). Days still to confirm are never in the heading:
 * they are one number of their own (`inoutDaysToConfirm`). An answer from one
 * rule (a minimum stay, a rule's days left) names it, counted, on the line.
 * `country` is the host country's name. A reading as of a past day (a closed
 * InOut, H7 — `pastDay`) says that day where a live one says "today".
 */
export function inoutAnswerText(
  evaluation: InOutEvaluation,
  {
    t,
    formatNumber,
    formatDate,
    country,
    pastDay = null,
  }: {
    t: TFunction;
    formatNumber: (value: number) => string;
    formatDate: (isoDate: string) => string;
    country: string;
    pastDay?: InOutPastDay;
  }
): { title: string; subtitle: string | null; sentence: string } {
  const answer = inoutAnswer(evaluation);
  if (answer.kind === 'oneTrip') {
    const said = { count: answer.days, days: formatNumber(answer.days), country };
    return {
      title: answer.underWay
        ? t('inout.answer.short.thisTrip', said)
        : t('inout.answer.short.oneTrip', said),
      subtitle: oneTripDetailText(answer, said, t, pastDay),
      sentence: inoutDaysAnswerText(answer, { t, formatNumber, country }),
    };
  }
  const { title, subtitle } = inoutRuleAnswerText(evaluation, answer, {
    t,
    formatNumber,
    formatDate,
    country,
    pastDay,
  });
  return { title, subtitle, sentence: title };
}

/**
 * The day a reading is said as of (InOut history, H7): null — today, the
 * InOut running, said "today" / "from today"; a day in words ("22 Oct 2026")
 * — a closed InOut, read as of the day it was closed, said on that day.
 */
export type InOutPastDay = string | null;

/** The line under a one-trip answer — from today, or from the day it is read as of. */
function oneTripDetailText(
  answer: Extract<InOutAnswer, { kind: 'oneTrip' }>,
  said: { count: number; days: string; country: string },
  t: TFunction,
  pastDay: InOutPastDay
): string {
  const outsideEu = answer.geography === 'OUTSIDE_EU';
  if (pastDay === null) {
    if (answer.underWay) {
      return outsideEu
        ? t('inout.answer.detail.thisTripOutsideEu', said)
        : t('inout.answer.detail.thisTrip', said);
    }
    return outsideEu
      ? t('inout.answer.detail.oneTripOutsideEu', said)
      : t('inout.answer.detail.oneTrip', said);
  }
  const dated = { ...said, date: pastDay };
  if (answer.underWay) {
    return outsideEu
      ? t('inout.answer.detail.thisTripOutsideEuOn', dated)
      : t('inout.answer.detail.thisTripOn', dated);
  }
  return outsideEu
    ? t('inout.answer.detail.oneTripOutsideEuFrom', dated)
    : t('inout.answer.detail.oneTripFrom', dated);
}

/** A rule's name in words — "Days away from Portugal"; `country` is the host country's name. */
export function inoutClockNameText(clock: InOutClock, t: TFunction, country: string): string {
  return t(inoutClockNameKey(clock), { country });
}

/** Every answer that is not one trip: one rule's, or none. */
function inoutRuleAnswerText(
  evaluation: InOutEvaluation,
  answer: Exclude<InOutAnswer, { kind: 'oneTrip' }>,
  {
    t,
    formatNumber,
    formatDate,
    country,
    pastDay,
  }: {
    t: TFunction;
    formatNumber: (value: number) => string;
    formatDate: (isoDate: string) => string;
    country: string;
    pastDay: InOutPastDay;
  }
): { title: string; subtitle: string | null } {
  const { firstToLimit } = inoutHeadlineView(evaluation);
  const nameOf = (clock: InOutClock) => inoutClockNameText(clock, t, country);
  // Why the rule that binds is the one to watch — named, with its day.
  const reasonOf = (clock: InOutClock) =>
    evaluation.binding?.constraintId === clock.constraintId
      ? t(INOUT_BINDING_REASON_KEY[evaluation.binding.reason], {
          rule: nameOf(clock),
          date: clock.exhaustsOn ? formatDate(clock.exhaustsOn) : '—',
        })
      : null;
  const first = firstToLimit ? reasonOf(firstToLimit) : null;
  // One rule's answer names the rule, counted — and a rule that comes first.
  const ruleLine = (clock: InOutCountedClock) => {
    const counted = t('inout.answer.headline', {
      rule: nameOf(clock),
      counted: inoutClockCountedText(clock, t, formatNumber),
    });
    return first ? t('inout.answer.headlineAndFirst', { headline: counted, first }) : counted;
  };
  switch (answer.kind) {
    case 'presenceNeeded':
      return {
        title: inoutDaysAnswerText(answer, { t, formatNumber, country }),
        subtitle: ruleLine(answer.clock),
      };
    case 'ruleLeft':
      return {
        title: t('inout.answer.headline', {
          rule: nameOf(answer.clock),
          counted: t('inout.clock.left', {
            count: answer.days,
            days: formatNumber(answer.days),
          }),
        }),
        subtitle: ruleLine(answer.clock),
      };
    case 'beyond':
      return {
        title: t('inout.answer.beyond', { rule: nameOf(answer.clock) }),
        subtitle: reasonOf(answer.clock),
      };
    case 'unresolved':
      return {
        title: t('inout.answer.unresolved'),
        subtitle: t('inout.banner.bindingUnresolved', { rule: nameOf(answer.clock) }),
      };
    case 'humanCheck':
      return { title: t('inout.answer.humanCheck'), subtitle: t('inout.banner.humanCheck') };
    case 'noRule':
      return {
        title:
          pastDay === null
            ? t('inout.banner.noBinding')
            : t('inout.banner.noBindingOn', { date: pastDay }),
        subtitle: null,
      };
    default: {
      const unreachable: never = answer;
      throw new Error(`Unknown InOut answer: ${String(unreachable)}`);
    }
  }
}

/** A duration in words — "6 months", "90 days", "6 years". */
function durationText(
  duration: Duration,
  t: TFunction,
  formatNumber: (value: number) => string
): string {
  return t(INOUT_DURATION_KEY[duration.unit], {
    count: duration.value,
    value: formatNumber(duration.value),
  });
}

/** The size of a window a rule names — a rolling one's, or a span from the title's issue. */
function windowSpan(window: InOutRuleWindow): Duration | null {
  if (window.months !== undefined) return { unit: 'months', value: window.months };
  if (window.years !== undefined) return { unit: 'years', value: window.years };
  if (window.days !== undefined) return { unit: 'days', value: window.days };
  return null;
}

/** Which words a window takes (`INOUT_METER_DETAIL_KEY`). */
function windowKindOf(window: InOutRuleWindow, span: Duration | null): InOutLimitWindowKind {
  switch (window.type) {
    case 'NONE':
      return 'none';
    case 'TITLE_VALIDITY':
      return window.resetsOnRenewal === false ? 'permitAndRenewals' : 'permit';
    case 'ROLLING':
      return 'rolling';
    case 'CALENDAR_YEAR':
      return 'calendarYear';
    case 'FIXED_FROM_ISSUE':
      // With no span of its own, it runs the title's validity (engine `scopeOf`).
      return span ? 'fromIssue' : 'permit';
    default: {
      const unreachable: never = window.type;
      throw new Error(`Unknown window ${String(unreachable)}`);
    }
  }
}

/**
 * What a rule with a number says, in its own terms:
 *
 *   total     the days counted of the limit, and what is left;
 *   eachTrip  a limit on every single absence (CONSECUTIVE, away): the
 *             longest so far against the limit, and how long one can be from
 *             today — two numbers that once read as one ("182 of 183 · 182
 *             left");
 *   needed    a minimum stay: the days counted of it, and those still needed.
 *
 * The figures are the engine's (`value`, `limit`, `remaining.certain`); this
 * only says which is which.
 */
export type InOutClockFigures =
  | { kind: 'total'; counted: number; limit: number; left: number | null }
  | { kind: 'eachTrip'; longest: number; limit: number; upTo: number | null }
  | { kind: 'needed'; counted: number; limit: number; needed: number | null };

export function inoutClockFigures(clock: InOutCountedClock): InOutClockFigures {
  const left = clock.remaining?.certain ?? null;
  if (clock.direction === 'MIN_PRESENCE') {
    return { kind: 'needed', counted: clock.value, limit: clock.limit, needed: left };
  }
  if (clock.accrual === 'CONSECUTIVE') {
    return { kind: 'eachTrip', longest: clock.value, limit: clock.limit, upTo: left };
  }
  return { kind: 'total', counted: clock.value, limit: clock.limit, left };
}

/**
 * What a rule has counted, in words — the part of its figures a card says
 * under the days left it shows in large: "183 of 244 days", "Longest so far:
 * 182 of 183 days", "3 of 7 days". Read as of a past day (a closed InOut, H7
 * — `pastDay`), the longest trip is the longest by that day ("Longest by
 * 22 Oct 2026: …") — "so far" would be false once days pass.
 */
export function inoutClockCountedText(
  clock: InOutCountedClock,
  t: TFunction,
  formatNumber: (value: number) => string,
  pastDay: InOutPastDay = null
): string {
  const figures = inoutClockFigures(clock);
  const limit = formatNumber(figures.limit);
  if (figures.kind !== 'eachTrip') {
    return t('inout.clock.counted', { value: formatNumber(figures.counted), limit });
  }
  const longest = { value: formatNumber(figures.longest), limit };
  return pastDay === null
    ? t('inout.clock.longestTrip', longest)
    : t('inout.clock.longestTripOn', { ...longest, date: pastDay });
}

/**
 * The figures of a rule with a number, in words — one sentence for the web
 * and the phone: "183 of 244 days · 61 left", "Longest so far: 182 of 183
 * days · from today, up to 182 days in a row away", "3 of 7 days · 4 still
 * needed" — "from 22 Oct 2026" for a reading as of a past day (`pastDay`).
 */
export function inoutClockFiguresText(
  clock: InOutCountedClock,
  t: TFunction,
  formatNumber: (value: number) => string,
  pastDay: InOutPastDay = null
): string {
  const figures = inoutClockFigures(clock);
  const counted = inoutClockCountedText(clock, t, formatNumber, pastDay);
  switch (figures.kind) {
    case 'total':
      return figures.left === null
        ? counted
        : `${counted} · ${t('inout.clock.left', { count: figures.left, days: formatNumber(figures.left) })}`;
    case 'eachTrip':
      return figures.upTo === null
        ? counted
        : `${counted} · ${
            pastDay === null
              ? t('inout.clock.tripUpTo', {
                  count: figures.upTo,
                  days: formatNumber(figures.upTo),
                })
              : t('inout.clock.tripUpToFrom', {
                  count: figures.upTo,
                  days: formatNumber(figures.upTo),
                  date: pastDay,
                })
          }`;
    case 'needed':
      return figures.needed === null
        ? counted
        : `${counted} · ${t('inout.clock.stillNeeded', { count: figures.needed, days: formatNumber(figures.needed) })}`;
    default: {
      const unreachable: never = figures;
      throw new Error(`Unknown clock figures: ${String(unreachable)}`);
    }
  }
}

/**
 * The days still to confirm — the days up to today the record does not say
 * for certain (`uncertainPeriods`): the person's to settle, one number for the
 * whole record, the same on every screen (owner, 22 Oct 2026 — the band, the
 * heading and the phone said ±1, 2 and 1 for the same days). What one rule's
 * count could still take from it is that rule's margin, said on its card.
 */
export function inoutDaysToConfirm(evaluation: Pick<InOutEvaluation, 'uncertainPeriods'>): number {
  return evaluation.uncertainPeriods.reduce((sum, period) => sum + period.days, 0);
}

/**
 * The record on a line — the window of the headline's rule (the title's two
 * years, say), the trips as bars (one still open runs to today;
 * a planned one is dashed by the screen), the days nobody confirmed shaded,
 * the renewal window and today. Every
 * position is a share of the window, 0–100: where a date falls, never a count
 * of days the engine did not make. No timeline without a window to draw.
 */
export interface InOutTimelineBar {
  key: string;
  kind: 'trip' | 'away' | 'planned';
  from: IsoDate;
  to: IsoDate | null;
  start: number;
  end: number;
}

export interface InOutTimeline {
  from: IsoDate;
  to: IsoDate;
  /** Where today falls — null when outside the window. */
  today: number | null;
  bars: InOutTimelineBar[];
  /** Days nobody confirmed (the engine's runs, a recorded trip's apart) — shaded, never a trip. */
  uncertain: Array<{ from: IsoDate; to: IsoDate; start: number; end: number }>;
  renewal: { start: number; end: number } | null;
}

export function inoutTimeline({
  evaluation,
  absences,
  plannedTrips,
}: {
  evaluation: InOutEvaluation;
  absences: ReadonlyArray<{ id: string; departedOn: IsoDate; returnedOn: IsoDate | null }>;
  plannedTrips: ReadonlyArray<{
    id: string;
    departsOn: IsoDate;
    returnsOn: IsoDate;
    state: string;
  }>;
}): InOutTimeline | null {
  const window = inoutHeadlineView(evaluation).clock?.window;
  if (!window) return null;
  const from = window.from;
  const to = window.to ?? evaluation.evaluatedOn;
  const scale = windowScale(from, to);
  if (!scale) return null;
  const { at, end, inWindow } = scale;

  const today = evaluation.evaluatedOn;
  const bars: InOutTimelineBar[] = [
    ...absences
      .filter((a) => inWindow(a.departedOn, a.returnedOn ?? today))
      .map((a) => ({
        key: a.id,
        kind: a.returnedOn === null ? ('away' as const) : ('trip' as const),
        from: a.departedOn,
        to: a.returnedOn,
        start: at(a.departedOn),
        end: end(a.returnedOn ?? today),
      })),
    ...plannedTrips
      .filter((p) => p.state === 'planned' && inWindow(p.departsOn, p.returnsOn))
      .map((p) => ({
        key: p.id,
        kind: 'planned' as const,
        from: p.departsOn,
        to: p.returnsOn,
        start: at(p.departsOn),
        end: end(p.returnsOn),
      })),
  ];
  const uncertain = evaluation.uncertainPeriods
    .filter((u) => !u.away && inWindow(u.from, u.to))
    .map((u) => ({ from: u.from, to: u.to, start: at(u.from), end: end(u.to) }));
  const renewal = evaluation.renewalWindow;
  return {
    from,
    to,
    today: scale.holds(today) ? at(today) : null,
    bars,
    uncertain,
    renewal:
      renewal && 'opensOn' in renewal && inWindow(renewal.opensOn, renewal.closesOn)
        ? { start: at(renewal.opensOn), end: end(renewal.closesOn) }
        : null,
  };
}

/**
 * A window of days as shares of a line, 0–100: where a day starts (`at`) and
 * ends (`end`), whether a span touches the window, whether a day is in it.
 */
function windowScale(from: IsoDate, to: IsoDate) {
  const first = dayOf(from);
  const span = dayOf(to) - first + 1;
  if (span <= 0) return null;
  const share = (day: number) => Math.max(0, Math.min(100, Math.round((day / span) * 1000) / 10));
  return {
    at: (date: IsoDate) => share(dayOf(date) - first),
    end: (date: IsoDate) => share(dayOf(date) - first + 1),
    inWindow: (start: IsoDate, finish: IsoDate) =>
      dayOf(finish) >= first && dayOf(start) <= first + span - 1,
    holds: (date: IsoDate) => dayOf(date) >= first && dayOf(date) < first + span,
  };
}

/**
 * A limit in total on its window's line (owner, 22 Oct 2026 — the 8 months on
 * the permit's validity): the window's first and last day, today, every span
 * of days the rule counted (`runs`, the engine's — no day counted here) and
 * the days nobody confirmed, as shares of the line. None for a window with no
 * end (it has no line to draw) or a rule with no number.
 */
export interface InOutLimitLine {
  from: IsoDate;
  to: IsoDate;
  today: number | null;
  runs: Array<{
    from: IsoDate;
    to: IsoDate;
    days: number;
    open: boolean;
    start: number;
    end: number;
  }>;
  uncertain: Array<{ from: IsoDate; to: IsoDate; days: number; start: number; end: number }>;
}

export function inoutLimitLine(
  clock: InOutClock,
  evaluation: Pick<InOutEvaluation, 'uncertainPeriods' | 'evaluatedOn'>
): InOutLimitLine | null {
  if (!inoutClockHasNumber(clock) || !clock.window?.to || !clock.runs) return null;
  const { from, to } = clock.window;
  const scale = windowScale(from, to);
  if (!scale) return null;
  return {
    from,
    to,
    today: scale.holds(evaluation.evaluatedOn) ? scale.at(evaluation.evaluatedOn) : null,
    runs: clock.runs.map((run) => ({ ...run, start: scale.at(run.from), end: scale.end(run.to) })),
    uncertain: evaluation.uncertainPeriods
      .filter((u) => scale.inWindow(u.from, u.to))
      .map((u) => ({
        from: u.from,
        to: u.to,
        days: u.days,
        start: scale.at(u.from),
        end: scale.end(u.to),
      })),
  };
}

/**
 * A limit in a row, trip by trip (owner, 22 Oct 2026 — the 6 months per
 * trip): each run the rule measured (`runs`, the engine's), the share of the
 * limit it took, and — when one trip from today is held under this limit by
 * the other one — how far the next trip can go (`next`), so the two limits
 * read together. None for a rule with no number.
 */
export interface InOutLimitTrips {
  limit: number;
  runs: Array<{ from: IsoDate; to: IsoDate; days: number; open: boolean; share: number }>;
  next: { days: number; share: number } | null;
}

export function inoutLimitTrips(
  clock: InOutClock,
  evaluation: InOutEvaluation
): InOutLimitTrips | null {
  if (!inoutClockHasNumber(clock) || !clock.runs) return null;
  const share = (days: number) => Math.min(1, days / clock.limit);
  const answer = inoutAnswer(evaluation);
  const capped =
    answer.kind === 'oneTrip' &&
    !answer.underWay &&
    answer.clock.constraintId !== clock.constraintId &&
    answer.days < clock.limit;
  return {
    limit: clock.limit,
    runs: clock.runs.map((run) => ({ ...run, share: share(run.days) })),
    next: capped ? { days: answer.days, share: share(answer.days) } : null,
  };
}

/**
 * The rules shown — every one that is the person's (NOT_APPLICABLE left out),
 * most relevant first — and those that are the stricter of two readings that
 * disagree, planned by.
 */
export function inoutShownClocks(evaluation: InOutEvaluation): {
  clocks: InOutClock[];
  plannedBy: ReadonlySet<string>;
} {
  return {
    clocks: inoutClocksByRelevance(evaluation),
    plannedBy: new Set(evaluation.divergences.map((d) => d.planningConstraintId)),
  };
}

/** A rule is the person's — shown — unless it does not apply to them. */
function isShown(clock: InOutClock): boolean {
  return clock.status !== 'NOT_APPLICABLE';
}

/**
 * The rules shown, the one that binds first ahead of the others — the order
 * of urgency the ladder of severity reads them in. Never a screen's order:
 * that is relevance (`inoutClocksByRelevance`).
 */
export function inoutClocksBindingFirst(
  evaluation: Pick<InOutEvaluation, 'clocks' | 'binding'>
): InOutClock[] {
  const clocks = evaluation.clocks.filter(isShown);
  const binding = evaluation.binding?.constraintId;
  return [
    ...clocks.filter((c) => c.constraintId === binding),
    ...clocks.filter((c) => c.constraintId !== binding),
  ];
}

/**
 * The long-term rights the record counts towards, each with its name — the
 * rules name every acquisition; one the engine counted that is not the
 * person's, or has no name, is left out.
 */
export function inoutAcquisitionRows(
  acquisition: readonly InOutAcquisitionClock[],
  labels: ReadonlyArray<{ id: string; label: string }>
): Array<{ clock: InOutAcquisitionClock; label: string }> {
  const labelOf = new Map(labels.map((a) => [a.id, a.label]));
  return acquisition.flatMap((clock) => {
    const label = labelOf.get(clock.acquisitionId);
    return clock.status !== 'NOT_APPLICABLE' && label ? [{ clock, label }] : [];
  });
}

/**
 * How far along a long-term right is (owner, 20 Oct 2026 — "Your milestones"
 * shows the way, as the limits show theirs): the years run since the
 * qualifying period began, of the years it takes, and the share of the way —
 * from the engine's own dates (`qualifyingFrom`, `completesOn`), today
 * included. None while the engine gives no dates (unresolved, not applicable).
 */
export function inoutAcquisitionProgress(
  clock: InOutAcquisitionClock,
  today: IsoDate
): { share: number; yearsDone: number; yearsTotal: number } | null {
  if (clock.status === 'UNRESOLVED' || !clock.qualifyingFrom || !clock.completesOn) return null;
  const from = dayOf(clock.qualifyingFrom);
  const to = dayOf(clock.completesOn);
  if (to <= from) return null;
  const done = Math.min(Math.max(dayOf(today) - from, 0), to - from);
  const DAYS_PER_YEAR = 365.25;
  return {
    share: done / (to - from),
    yearsDone: Math.floor(done / DAYS_PER_YEAR),
    yearsTotal: Math.round((to - from) / DAYS_PER_YEAR),
  };
}

/**
 * The first long-term right the record completes — its name and the day —
 * for a summary's line of milestones; none while no right has a day yet.
 */
export function inoutNextAcquisition(
  acquisition: readonly InOutAcquisitionClock[],
  labels: ReadonlyArray<{ id: string; label: string }>
): { label: string; completesOn: IsoDate } | null {
  return inoutAcquisitionRows(acquisition, labels).reduce<{
    label: string;
    completesOn: IsoDate;
  } | null>((first, { clock, label }) => {
    if (clock.status === 'UNRESOLVED' || !clock.completesOn) return first;
    return first === null || clock.completesOn < first.completesOn
      ? { label, completesOn: clock.completesOn }
      : first;
  }, null);
}

/**
 * The 90/180 explained where it applies (owner, 20 Oct 2026): the three places
 * a person can be — in the title's country, in another Schengen state, outside
 * Schengen — whether a day there counts as away and towards the 90/180, and
 * the one the engine says the person is in today (`whereToday`), marked. One
 * table for Citizen Web and the phone.
 */
export function inoutShortStayRows(whereToday: InOutWhereToday): Array<{
  where: Exclude<InOutWhereToday, 'UNKNOWN'>;
  away: boolean;
  shortStay: boolean;
  today: boolean;
}> {
  return (
    [
      { where: 'HOST', away: false, shortStay: false },
      { where: 'OTHER_SCHENGEN', away: true, shortStay: true },
      { where: 'OUTSIDE_SCHENGEN', away: true, shortStay: false },
    ] as const
  ).map((row) => ({ ...row, today: row.where === whereToday }));
}

/**
 * The rule people know as "the 90/180" (art. 21 CISA): days in the Schengen
 * states other than the title's — the one a person brings the wrong idea of,
 * so its row offers the explainer of where it applies and where it does not.
 */
export function inoutIsShortStayRule(clock: Pick<InOutClock, 'geography'>): boolean {
  return clock.geography === 'SCHENGEN_EXCLUDING_HOST';
}
